import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import Alert from '../components/ui/Alert.jsx';
import CopyButton from '../components/ui/CopyButton.jsx';
import CopyRichButton from '../components/ui/CopyRichButton.jsx';
import Modal from '../components/ui/Modal.jsx';
import { HowThisPageWorks, Loading, Section } from '../components/ui/index.jsx';
import ScoreGauge, { scoreColour } from '../components/ui/ScoreGauge.jsx';
import { FinalVerdict } from './inbox/inboxShared.jsx';
import { RegisteredAgo, SOURCE, SiteStatus, defangDomain } from './lookalikes/lookalikeShared.jsx';
import { fmtDateTime } from '../utils/format.js';
import { fmtMinutes, nextStep, planSteps } from './home/planSteps.js';
import { NewStampModal, StampReadyModal } from './events/StampsSection.jsx';
import SetupPrompt from '../components/SetupPrompt.jsx';

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const fmtPts = (v) => (Number.isInteger(v) ? v : v.toFixed(1));

/** 30 days of scores as a small line. */
export function Trend({ history }) {
  if (history.length < 2) return <p className="muted small">Your trend appears here from tomorrow.</p>;
  const w = 220;
  const h = 44;
  const xs = (i) => (i / (history.length - 1)) * w;
  const ys = (v) => h - 4 - (v / 100) * (h - 8);
  const d = history.map((p, i) => `${i ? 'L' : 'M'}${xs(i).toFixed(1)} ${ys(p.score).toFixed(1)}`).join(' ');
  const first = history[0].score;
  const last = history.at(-1).score;
  const change = last - first;
  return (
    <div className="trend">
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} aria-hidden="true" preserveAspectRatio="none">
        <path d={d} fill="none" stroke="var(--teal-ink)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
      <p className="muted small">
        {change === 0 ? 'No change' : `${change > 0 ? 'Up' : 'Down'} ${Math.abs(change)} point${Math.abs(change) === 1 ? '' : 's'}`} over the last {history.length} days
      </p>
    </div>
  );
}


/** "Warn my exhibitors": a ready-made email they send from their own system (we never email their contacts). */
function WarnExhibitorsModal({ organisationId, onClose }) {
  const [eventId, setEventId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api(`/portal/organisations/${organisationId}/warnings/exhibitor-email${eventId ? `?event=${eventId}` : ''}`).then(setData).catch((err) => setError(err.message));
  }, [organisationId, eventId]);
  const email = data?.email;
  const mailto = email ? `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.text)}` : null;
  return (
    <Modal title="Warn your exhibitors" size="lg" onClose={onClose}
      description="An email to send your exhibitors from your own email or newsletter tool. It covers the scams active against events now and points to your verify page. We never email your exhibitors ourselves."
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>}>
      {error && <Alert tone="danger">{error}</Alert>}
      {!data ? <Loading /> : !email ? <p className="muted">Add an upcoming event first: the email links to its verify page.</p> : (
        <>
          {data.events.length > 1 && (
            <div className="field">
              <label htmlFor="warn-event">Event</label>
              <select id="warn-event" value={data.event_id} onChange={(e) => setEventId(e.target.value)}>
                {data.events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </select>
            </div>
          )}
          <p className="small"><strong>Subject:</strong> {email.subject} <CopyButton text={email.subject} label="Copy subject" /></p>
          <div className="warn-email-preview" dangerouslySetInnerHTML={{ __html: email.html }} />
          <div className="takedown-actions">
            <CopyRichButton html={email.html} text={email.text} label="Copy the email" primary />
            <CopyButton text={email.text} label="Copy as plain text" />
            <a className="btn btn-small btn-secondary" href={mailto}>Open in my email</a>
          </div>
          <p className="muted small">Paste it into your newsletter tool, or send it to your exhibitors in Bcc so they don’t see each other’s addresses.</p>
        </>
      )}
    </Modal>
  );
}

/** Scam campaigns seen at several events we protect this week (no company named), and the weekly email switch. */
function ActiveCampaigns({ organisationId }) {
  const [data, setData] = useState(null);
  const [warning, setWarning] = useState(false);
  const load = () => api(`/portal/organisations/${organisationId}/warnings`).then(setData).catch(() => setData(null));
  useEffect(() => { load(); }, [organisationId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const toggle = async (e) => {
    try { await api(`/portal/organisations/${organisationId}/warnings`, { method: 'PUT', body: { early_warnings: e.target.checked } }); load(); } catch { /* shown unchanged */ }
  };
  const label = (c) => (c.label ? c.label[0].toUpperCase() + c.label.slice(1) : c.kind === 'phishing' ? 'Phishing' : 'Pretending to be the organisers');
  return (
    <Section title="Active against events now" description="Scams confirmed at several events we protect in the last week. We never share which events, or anything from their emails."
      actions={<button type="button" className="btn btn-secondary btn-small" onClick={() => setWarning(true)}>Warn my exhibitors</button>}>
      {warning && <WarnExhibitorsModal organisationId={organisationId} onClose={() => setWarning(false)} />}
      {data.campaigns.length ? (
        <ul className="campaign-list">
          {data.campaigns.map((c, i) => (
            <li key={`${c.pattern || c.kind}-${i}`}>
              <strong>{label(c)}</strong> <span className="muted small">· seen at {c.companies} events</span>
              <p className="small">{c.advice}</p>
            </li>
          ))}
        </ul>
      ) : <p className="muted">Nothing seen at several events this week.</p>}
      {data.can_manage && (
        <label className="takedown-auto small"><input type="checkbox" checked={data.early_warnings} onChange={toggle} /> Email us a summary each Monday while we have an event coming up</label>
      )}
    </Section>
  );
}

/** The newest lookalike sites found (variations, certificates, web search, listings), with a link to the rest. */
function LatestLookalikes({ organisationId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api(`/portal/organisations/${organisationId}/lookalikes?view=all&sort=recent`).then(setData).catch((err) => setError(err.message));
  }, [organisationId]);
  const sites = (data?.sites || []).slice(0, 8);
  const needs = data?.counts?.needs || 0;
  return (
    <Section title="Latest fake websites" flush={sites.length > 0}
      description={data ? `${data.counts.total} watched${needs ? ` · ${needs} need a look` : ''}${data.last_checked ? ` · last checked ${fmtDateTime(data.last_checked)}` : ''}` : null}
      actions={<Link to={needs ? '/company/lookalikes?view=needs' : '/company/lookalikes?view=all'} className="btn btn-secondary btn-small">{needs ? `Look at ${needs}` : 'All fake websites'}</Link>}>
      {error ? <Alert tone="danger">{error}</Alert> : !data ? <Loading /> : sites.length === 0 ? (
        <p className="muted">{data.has_verified_domain ? 'Nothing found yet. We look for typos of your domains, new certificates and web pages using your name every day.' : <>Verify one of your domains and we start looking for sites made to look like it. <Link to="/company">Verify a domain</Link></>}</p>
      ) : (
        <div className="table-scroll">
          <table className="table table-stack dash-latest">
            <thead><tr><th>Site</th><th>What it is</th></tr></thead>
            <tbody>
              {sites.map((x) => (
                <tr key={x.id}>
                  <td data-label="Site">
                    <Link className="strong-link" to={`/company/lookalikes/${x.id}`}>
                      {x.listing ? <><strong>{x.listing.platform} listing</strong> <span className="mono small">{defangDomain(x.domain)}</span></> : <span className="mono">{defangDomain(x.domain)}</span>}
                    </Link>
                    {(x.listing?.name || x.title) && <div className="small truncate-1">{x.listing?.name || x.title}</div>}
                    <div className="muted small">
                      Found {fmtDateTime(x.created_at)} · {SOURCE[x.source].toLowerCase()}{x.looks_like ? ` (${x.looks_like})` : ''}
                      {x.registered_at && !x.listing && <> · registered <RegisteredAgo at={x.registered_at} short /></>}
                    </div>
                  </td>
                  <td data-label="What it is" className="nowrap"><SiteStatus site={x} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/** The ten newest emails in the Inbox, with a link to the rest. */
function LatestEmails({ organisationId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api(`/portal/organisations/${organisationId}/inbox`).then(setData).catch((err) => setError(err.message));
  }, [organisationId]);
  const latest = (data?.messages || []).slice(0, 10);
  return (
    <Section title="Latest messages" flush={latest.length > 0}
      actions={<Link to="/inbox" className="btn btn-secondary btn-small">See all messages</Link>}>
      {error ? <Alert tone="danger">{error}</Alert> : !data ? <Loading /> : latest.length === 0 ? (
        <p className="muted">Nothing forwarded to you yet. Emails people forward to your events’ check addresses appear here.</p>
      ) : (
        <div className="table-scroll">
          <table className="table table-stack dash-latest">
            <thead><tr><th>Received</th><th>Email</th><th>Verdict</th></tr></thead>
            <tbody>
              {latest.map((m) => (
                <tr key={m.id}>
                  <td data-label="Received" className="nowrap muted small">{fmtDateTime(m.received_at)}</td>
                  <td data-label="Email">
                    <Link className="strong-link" to={`/inbox/${m.id}`}>{m.subject || '(no subject)'}</Link>
                    <div className="muted small">{m.from_address ? `From ${m.from_name || m.from_address}` : 'Sender unknown'}{m.event_name && <> · {m.event_name}</>}</div>
                  </td>
                  <td data-label="Verdict"><FinalVerdict verdict={m.verdict} decision={m.decision} status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

export function PartBar({ part }) {
  const pct = (part.points / part.max) * 100;
  return (
    <div className="part">
      <div className="part-head">
        <h3>{part.label}{part.subject && <span className="muted"> · {part.subject}</span>}</h3>
        <span className="part-points">{fmtPts(part.points)} / {part.max}</span>
      </div>
      <div className="part-bar" aria-hidden="true"><span style={{ width: `${pct}%`, background: scoreColour(pct) }} /></div>
      {part.note && <p className="muted small">{part.note}</p>}
      <ul className="part-items">
        {part.items.map((i) => (
          <li key={i.key} className={i.done ? 'is-done' : ''}>
            <span className="part-mark" aria-label={i.done ? 'Done' : 'Not yet'}>{i.done ? '✓' : '○'}</span>
            <span className="part-item-label">{i.label}</span>
            <span className="part-item-pts">{fmtPts(i.points)}/{i.max}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const ICON = {
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" /></>,
  code: <><path d="M8 8l-4 4 4 4" /><path d="M16 8l4 4-4 4" /></>,
  lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 7.5-2" /></>,
  shield: <><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" /><path d="M9 12l2 2 4-4" /></>,
  warn: <><path d="M12 3l9 16H3L12 3z" /><path d="M12 10v4" /><path d="M12 17h.01" /></>,
  stamp: <><path d="M9 4h6v3a3 3 0 0 1-1 2.2V12h4a2 2 0 0 1 2 2v3H4v-3a2 2 0 0 1 2-2h4V9.2A3 3 0 0 1 9 7V4z" /><path d="M4 20h16" /></>,
};
const Icon = ({ name, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICON[name]}</svg>
);

/**
 * The quick way to the most useful thing we offer: stamping an important email before it
 * goes out, so a scammer copying it is caught. Opens straight into making one.
 */
function StampCard({ events, next }) {
  const [making, setMaking] = useState(false);
  const [ready, setReady] = useState(null);   // { stamp, eventId }
  const [inUse, setInUse] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const choices = events.filter((e) => !e.ends_on || e.ends_on >= today);
  const pick = choices.length ? choices : events;
  useEffect(() => {
    if (!next) return;
    api(`/portal/events/${next.id}/stamps`).then((d) => setInUse((d.stamps || []).filter((x) => ['unbound', 'bound'].includes(x.state)).length)).catch(() => setInUse(null));
  }, [next, ready]);
  return (
    <section className="card home-stamp" aria-labelledby="stamp-h">
      <div className="home-stamp-head">
        <span className="home-stamp-icon"><Icon name="stamp" size={26} /></span>
        <h2 id="stamp-h">Stamp an email</h2>
      </div>
      <p>Sending an invoice, payment reminder or exhibitor manual? Stamp it first. People can click the stamp to see your real email, and if a scammer copies it, we catch it.</p>
      {next ? (
        <>
          <button type="button" className="btn btn-primary home-stamp-btn" onClick={() => setMaking(true)}>Stamp an email</button>
          <p className="small home-stamp-foot">
            {inUse ? `${inUse} stamp${inUse === 1 ? '' : 's'} in use for ${next.name}. ` : ''}
            <Link to={`/events/${next.id}`} className="strong-link-teal">Your stamps →</Link>
          </p>
          <a href="/stamp/" target="_blank" rel="noreferrer" className="btn btn-secondary btn-small home-stamp-app">Get the desktop app</a>
          <p className="small muted home-stamp-app-note">Quick Stamp: stamp emails and report scams in two clicks, from Windows, Mac or your phone.</p>
        </>
      ) : <Link to="/events" className="btn btn-primary home-stamp-btn">Add your event first</Link>}
      {making && <NewStampModal events={pick} onClose={() => setMaking(false)} onMade={(stamp, eventId) => { setMaking(false); setReady({ stamp, eventId }); }} />}
      {ready && <StampReadyModal stamp={ready.stamp} eventId={ready.eventId} onClose={() => setReady(null)} />}
    </section>
  );
}

/** What needs the company now, in plain words: decisions first, then the plan's next step. */
function needsItems(stats, step, lookalikeNeeds) {
  const a = stats.alerts;
  const items = [];
  const plural = (n, one, many) => (n === 1 ? one : many.replace('#', n));
  if (a.emails_to_decide > 0) {
    items.push({ key: 'emails', tone: a.likely_scams_to_decide ? 'bad' : 'warn', icon: 'mail', to: '/inbox?decision=none', cta: 'Look at it',
      title: a.likely_scams_to_decide ? plural(a.likely_scams_to_decide, 'An email pretending to be you', '# emails pretending to be you') : plural(a.emails_to_decide, 'An email we need you to look at', '# emails we need you to look at'),
      text: a.forwarders_waiting ? `${a.forwarders_waiting === 1 ? 'Someone is' : `${a.forwarders_waiting} people are`} waiting to hear if it’s really from you.` : 'Tell us if it’s really from you, so we can answer next time.' });
  }
  if (lookalikeNeeds > 0) {
    items.push({ key: 'sites', tone: 'bad', icon: 'globe', to: '/company/lookalikes?view=needs', cta: 'See it',
      title: plural(lookalikeNeeds, 'A website pretending to be you', '# websites pretending to be you'),
      text: 'It copies your site or uses your name. Tell us if it’s fake and we’ll warn everyone who checks.' });
  }
  if (a.copy_sites_open > 0) {
    items.push({ key: 'code', tone: 'warn', icon: 'code', to: '/company/website', cta: 'Check it',
      title: plural(a.copy_sites_open, 'A website running your code', '# websites running your code'),
      text: 'We don’t know it’s yours. It may be a copy of your site.' });
  }
  if (step) {
    items.push({ key: 'plan', tone: 'warn', icon: 'lock', to: '/plan', cta: 'Show me how', secondary: true,
      title: step.title, text: `${step.why.split('. ')[0].replace(/\.$/, '')}. About ${fmtMinutes(step.minutes)}.` });
  }
  return items;
}

const HERO = {
  safe: { icon: 'shield', title: 'You’re protected', body: 'Nothing needs you right now. We’re checking messages and watching for fake websites around the clock.' },
  // Worded from the protection level, so the banner and the dial never disagree
  attention: { icon: 'warn', title: (n, level) => `${level >= 70 ? 'Mostly protected' : level >= 40 ? 'Partly protected' : 'Not well protected yet'}, ${n} thing${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} you`,
    body: (n, level) => (level >= 70 ? `Do ${n === 1 ? 'this' : 'these'} and you’re fully protected.` : `Start with ${n === 1 ? 'this' : 'these'}. Your protection plan has the rest.`) },
  danger: { icon: 'warn', title: 'Someone is pretending to be you', body: 'People have been sent something fake in your name. Tell us what it is and we’ll warn everyone who checks.' },
};

export default function Dashboard() {
  const { organisation, user } = useAuth();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [lookalikes, setLookalikes] = useState(null);
  const [warning, setWarning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!organisation) return;
    api(`/portal/organisations/${organisation.id}/dashboard`).then(setData).catch((err) => setError(err.message));
    api('/portal/events').then((r) => setEvents((r.events || []).filter((e) => e.organisation_id === organisation.id))).catch(() => setEvents([]));
    api(`/portal/organisations/${organisation.id}/lookalikes?view=needs`).then((r) => setLookalikes(r.counts)).catch(() => setLookalikes(null));
  }, [organisation]);

  if (error) return <div className="page"><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  const { score, stats } = data;
  const steps = planSteps(score);
  const step = score.score < 90 ? nextStep(steps) : null;
  const items = needsItems(stats, step, lookalikes?.needs || 0);
  const state = stats.alerts.likely_scams_to_decide > 0 || (lookalikes?.needs || 0) > 0 ? 'danger' : items.length ? 'attention' : 'safe';
  const minutes = items.reduce((t, i) => t + (i.key === 'plan' ? step.minutes : 3), 0);
  const done = steps.filter((x) => x.done).length;
  const today = new Date().toISOString().slice(0, 10);
  const next = events.find((e) => !e.ends_on || e.ends_on >= today) || events[0] || null;
  const firstName = user?.first_name || '';
  const hero = HERO[state];

  return (
    <div className="page home">
      <div className="page-header-top"><span /><HowThisPageWorks topic="home" /></div>
      <div>
        <p className="muted home-greeting">{greeting()}{firstName ? `, ${firstName}` : ''}</p>
        <h1>{next ? <>{next.name}{next.starts_on ? <span className="home-when"><span className="home-dot" aria-hidden="true"> · </span>{fmtEventDates(next)}</span> : ''}</> : organisation.name}</h1>
      </div>

      {score.test_mode && (
        <Alert tone="warn">
          You&rsquo;re still being set up: your check page isn&rsquo;t public yet. <Link to="/company">Prove your domain is yours</Link> to switch it on.
        </Alert>
      )}

      <section className={`home-hero is-${state}`} aria-labelledby="hero-h">
        <div className="home-hero-icon"><Icon name={hero.icon} size={48} /></div>
        <div className="home-hero-text">
          <h2 id="hero-h">{state === 'attention' ? hero.title(items.length, score.score) : hero.title}</h2>
          <p>{state === 'attention' ? `${hero.body(items.length, score.score)} About ${fmtMinutes(minutes)}.` : hero.body}</p>
        </div>
        {items[0] && <Link to={items[0].to} className="btn btn-primary home-hero-cta">{state === 'danger' ? 'Deal with it now' : 'Start with the first one'}</Link>}
        {!items[0] && <Link to="/plan" className="btn btn-primary home-hero-cta">See what we watch</Link>}
      </section>

      <SetupPrompt />

      <div className="home-grid">
        <section className="card home-needs" aria-labelledby="needs-h">
          <div className="home-card-head">
            <h2 id="needs-h">Needs you</h2>
            {items.length > 0 && <span className="muted">{items.length} thing{items.length === 1 ? '' : 's'} · about {fmtMinutes(minutes)}</span>}
          </div>
          {items.length ? (
            <ul className="needs-rows">
              {items.map((i) => (
                <li key={i.key}>
                  <span className={`needs-icon tone-${i.tone}`}><Icon name={i.icon} /></span>
                  <div className="needs-text">
                    <strong>{i.title}</strong>
                    <span className="muted">{i.text}</span>
                  </div>
                  <Link to={i.to} className={`btn ${i.secondary ? 'btn-secondary' : 'btn-primary'} btn-small`}>{i.cta}</Link>
                </li>
              ))}
            </ul>
          ) : <p className="muted">Nothing right now. We&rsquo;ll email you the moment something does.</p>}
        </section>

        <aside className="home-side">
          <StampCard events={events} next={next} />
          <section className="card" aria-labelledby="plan-h">
            <h2 id="plan-h">Your protection plan</h2>
            <div className="home-gauge"><ScoreGauge score={score.score} label={score.band.label} /></div>
            <p className="muted">{done} of {steps.length} steps done</p>
            <div className="plan-bar" role="img" aria-label={`${done} of ${steps.length} steps done`}><span style={{ width: `${steps.length ? (done / steps.length) * 100 : 0}%` }} /></div>
            {step ? <p className="small home-next">Next: <strong>{step.title}</strong></p> : <p className="small home-next">Every step done.</p>}
            <Link to="/plan" className="strong-link-teal">{step ? 'Continue your plan →' : 'See your plan →'}</Link>
          </section>

        </aside>
      </div>

      {next && (
        <section className="card home-checkpage" aria-labelledby="check-h">
          <div className="home-checkpage-text">
            <h2 id="check-h">Your check page</h2>
            <p>Exhibitors and attendees check emails and calls here before they pay or reply.</p>
          </div>
          <div className="home-checkpage-link">
            <p className="home-checkpage-url">{next.verify_url.replace(/^https?:\/\//, '')}</p>
            <div className="home-checkpage-actions">
              <CopyButton text={next.verify_url} label="Copy link" />
              <button type="button" className="btn btn-small btn-on-dark" onClick={() => setWarning(true)}>Tell my exhibitors</button>
            </div>
          </div>
        </section>
      )}

      <section aria-labelledby="watch-h">
        <h2 id="watch-h">What we&rsquo;re watching for you</h2>
        <div className="watch-grid">
          <Link to="/inbox" className="watch-tile">
            <span className="muted">Messages checked</span>
            <strong>{n(stats.checks.total)}</strong>
            <span className={stats.checks.not_official ? 'tone-text-good' : 'muted'}>{stats.checks.not_official ? `${n(stats.checks.not_official)} fake${stats.checks.not_official === 1 ? '' : 's'} caught in 30 days` : 'No fakes in 30 days'}</span>
          </Link>
          <Link to="/company/lookalikes" className="watch-tile">
            <span className="muted">Fake websites</span>
            <strong>{lookalikes ? (lookalikes.needs ? `${n(lookalikes.needs)} new` : 'None') : '—'}</strong>
            <span className="muted">{lookalikes ? `${n(lookalikes.total)} look-alike name${lookalikes.total === 1 ? '' : 's'} watched` : ''}</span>
          </Link>
          <Link to="/plan" className="watch-tile">
            <span className="muted">Your email address</span>
            {emailFakeable(score) ? <strong className="watch-word tone-text-warn">Can be faked</strong> : <strong className="watch-word tone-text-good">Protected</strong>}
            <span className="muted">{emailFakeable(score) ? 'One setting to change' : 'Fakes are refused'}</span>
          </Link>
          <Link to="/events" className="watch-tile">
            <span className="muted">Check page</span>
            {score.test_mode ? <strong className="watch-word tone-text-warn">Not public yet</strong> : <strong className="watch-word tone-text-good">Live</strong>}
            <span className="muted">{n(stats.checks.verify_page)} check{stats.checks.verify_page === 1 ? '' : 's'} in 30 days</span>
          </Link>
        </div>
      </section>

      <div className="dash-latest-grid">
        <LatestEmails organisationId={organisation.id} />
        <LatestLookalikes organisationId={organisation.id} />
      </div>

      <ActiveCampaigns organisationId={organisation.id} />

      {warning && <WarnExhibitorsModal organisationId={organisation.id} onClose={() => setWarning(false)} />}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}
function fmtEventDates(e) {
  const d = (x) => new Date(`${x}T00:00:00Z`);
  const f = (x, o) => d(x).toLocaleDateString('en-GB', { timeZone: 'UTC', ...o });
  if (!e.ends_on || e.ends_on === e.starts_on) return f(e.starts_on, { day: 'numeric', month: 'long' });
  if (e.starts_on.slice(0, 7) === e.ends_on.slice(0, 7)) return `${f(e.starts_on, { day: 'numeric' })}–${f(e.ends_on, { day: 'numeric', month: 'long' })}`;
  return `${f(e.starts_on, { day: 'numeric', month: 'long' })} – ${f(e.ends_on, { day: 'numeric', month: 'long' })}`;
}
/** Can a scammer fake their email address? (No DMARC policy that refuses or quarantines.) */
function emailFakeable(score) {
  const dmarc = score.parts.find((p) => p.key === 'email')?.items.find((i) => i.key === 'dmarc');
  return !dmarc || dmarc.points < 9;
}
